import cx from 'classnames';
import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmModal, DataWrapper, StarRating, TextArea} from 'components';
import {FEEDBACK_COMMENT_MAX_LENGTH} from 'constants/session';
import {SessionFeedback, SessionFeedbackAvailability} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

type Props = {
    open: boolean;
    loading: boolean;
    availability: SessionFeedbackAvailability;
    onClose: (confirm: boolean, data: SessionFeedback) => void;
}

const defaultValues = {
    rating: null,
    comment: ''
};

export const SessionFeedbackModal: React.FC<Props> = ({open, loading, availability, onClose}: Props) => {
    const {t} = useTranslation();
    const [initialState, setInitialState] = useState(defaultValues);
    const [rating, setRating] = useState(initialState.rating);
    const [comment, setComment] = useState(initialState.comment);

    const handleClose = useCallback((confirm: boolean, data: SessionFeedback) => {
        if (!confirm) {
            setRating(initialState.rating);
            setComment(initialState.comment);
        } else {
            setInitialState({rating, comment});
        }
        onClose(confirm, data);
    }, [initialState, rating, comment, onClose]);

    return (
        <ConfirmModal
            title={<div className='tw-text-center'>{t(TRANSLATION_KEYS.COMMON.SESSION.FEEDBACK.ADD_TITLE)}</div>}
            open={open}
            fullWidth
            maxWidth='xss'
            data={{
                ...(availability.rating && {rating}),
                ...(availability.comment && {comment}),
            }}
            contentClassName='tw-relative'
            okText={t(TRANSLATION_KEYS.COMMON.BUTTONS.SAVE)}
            okDisabled={(!rating && !comment) || loading}
            onClose={handleClose}
        >
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                {availability.rating &&
                    <div className='tw-text-center'>
                        <StarRating
                            value={rating}
                            size='large'
                            onChange={setRating}
                        />
                    </div>}
                {availability.comment &&
                    <TextArea
                        value={comment}
                        disabled={loading}
                        className={cx({'tw-mt-5': availability.rating})}
                        label={t(TRANSLATION_KEYS.COMMON.SESSION.LABELS.COMMENTS)}
                        rows={4}
                        maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
                        onChange={setComment}
                    />}
            </DataWrapper>
        </ConfirmModal>
    );
};